//libs
import React, { useCallback, useRef, useState } from 'react';
import c from 'classnames';

import { useTranslation } from '@lyearn/i18n';
import { ArrowIosDownward, CheckmarkOutline, CircleFill, NoneOutlineLegacy } from '@lyearn/icons';
//hooks
import { useStorageContext } from '@lyearn/lygo-core';
//components
import {
  BasePopoverContent,
  Box,
  DeprecatedIconButton,
  PopoverOrigin,
  Stack,
  Typography,
} from '@lyearn/ui';

//conts
import { LAST_USED_COLOR_KEY } from '../../../../plugins/SelectionMenu/consts';
//types
import { MarkChangeActions, MarkTypesEnum } from '../../../../plugins/SelectionMenu/types';
//utils
import { BACKGROUND_COLORS, FONT_COLORS } from './consts';
//styles
import { iconButtonMenu, listItem, popoverContent, setColor } from './styles';

const ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: 'center',
  horizontal: 'right',
};

const TRANSFORM_ORIGIN: PopoverOrigin = {
  vertical: 'center',
  horizontal: 'left',
};

const CLASSES = {
  paper: c('rounded-8 ms-12 px-8 py-16  shadow-01 border border-divider', popoverContent),
};

interface ColorMenuProps {
  selectedColor?: string;
  selectedBackgroundColor?: string;
  onMarkChangeAction: (action: MarkChangeActions) => void;
}

const renderListTitle = (title: string) => (
  <Typography className="px-12 pb-12 text-start" color="text-text-placeholder" variant="label-caps">
    {title}
  </Typography>
);

const renderListDivider = () => (
  <Box className="my-16 w-full px-8">
    <Box className="h-1 bg-border" />
  </Box>
);

const renderColorList = ({
  title,
  selectedColor,
  colors,
  onChange,
}: {
  title: string;
  selectedColor?: string;
  colors: {
    label: string;
    hexValue: string;
  }[];
  onChange: (hexValue: string) => void;
}) => {
  return (
    <>
      {renderListTitle(title)}
      {colors.map((color, index) => (
        <Stack
          key={color.hexValue}
          alignItems="center"
          className={c(
            'cursor-pointer rounded-8 py-10 px-10 hover:bg-interactive-secondary',
            listItem,
            {
              'mt-12': index === 0,
            },
          )}
          justifyContent="between"
          role="buttom"
          onClick={() => {
            onChange(color.hexValue);
          }}>
          <Stack alignItems="center">
            {color.hexValue === 'transparent' ? (
              <NoneOutlineLegacy className="h-20 w-20 text-icon-placeholder" />
            ) : (
              <CircleFill className={c('h-20 w-20', setColor(color.hexValue))} />
            )}
            <Typography className="ms-10" color="text-text-secondary" variant="body-short-02">
              {color.label}
            </Typography>
          </Stack>
          {color.hexValue === selectedColor && <CheckmarkOutline className="h-20 w-20" />}
        </Stack>
      ))}
    </>
  );
};

export const ColorMenu: React.FC<ColorMenuProps> = (props) => {
  const { selectedColor, selectedBackgroundColor, onMarkChangeAction } = props;
  const buttonRef = useRef<null | HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { storage } = useStorageContext();
  const { t } = useTranslation();
  const { color: lastUsedColor } = storage?.getData<{ color: string }>(LAST_USED_COLOR_KEY) || {
    color: '',
  };

  const onColorChange = useCallback(
    (color: string) => {
      onMarkChangeAction({
        type: MarkTypesEnum.Color,
        payload: {
          color,
        },
      });
    },
    [onMarkChangeAction],
  );

  const onBackgroundChange = useCallback(
    (backgroundColor: string) => {
      onMarkChangeAction({
        type: MarkTypesEnum.Color,
        payload: {
          backgroundColor,
        },
      });
    },
    [onMarkChangeAction],
  );

  const renderLastUsed = () => {
    if (!lastUsedColor) {
      return null;
    }
    if (lastUsedColor) {
      return (
        <>
          {renderListTitle('Last Used')}
          <Stack
            alignItems="center"
            className={c(
              'mt-12 cursor-pointer rounded-8 py-10 px-10 hover:bg-interactive-secondary',
              listItem,
            )}
            justifyContent="between"
            role="buttom"
            onClick={() => {
              onColorChange(lastUsedColor);
            }}>
            <Stack alignItems="center">
              <CircleFill className={c('h-20 w-20', setColor(lastUsedColor))} />
              <Typography className="ms-10" color="text-text-secondary" variant="body-short-02">
                {FONT_COLORS(t).find((color) => color.hexValue === lastUsedColor)?.label}
              </Typography>
            </Stack>
          </Stack>
          {renderListDivider()}
        </>
      );
    }
  };

  return (
    <>
      <DeprecatedIconButton
        ref={buttonRef}
        className={c(iconButtonMenu, 'm-6 w-48 rounded-4 py-4 pe-2 ps-4 ms-8')}
        variant="ghost"
        onClick={() => setOpen(true)}>
        <CircleFill
          className={setColor(
            selectedBackgroundColor !== 'transparent' ? selectedBackgroundColor : selectedColor,
          )}
        />
        <ArrowIosDownward className="chevron text-icon-secondary ms-2" />
      </DeprecatedIconButton>
      <BasePopoverContent
        anchorEl={buttonRef.current}
        anchorOrigin={ANCHOR_ORIGIN}
        classes={CLASSES}
        open={open}
        transformOrigin={TRANSFORM_ORIGIN}
        onClose={() => setOpen(false)}>
        {renderLastUsed()}
        {renderColorList({
          title: 'Text',
          selectedColor,
          colors: FONT_COLORS(t),
          onChange: onColorChange,
        })}
        {renderListDivider()}
        {renderColorList({
          title: 'Background',
          selectedColor: selectedBackgroundColor,
          onChange: onBackgroundChange,
          colors: BACKGROUND_COLORS(t),
        })}
      </BasePopoverContent>
    </>
  );
};

export default ColorMenu;
